import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

class PropertyAnalyticsService {
  // Track a property view
  async trackPropertyView(propertyId) {
    try {
      const userId = localStorage.getItem('userId') || 'anonymous';
      
      await addDoc(collection(db, 'property_analytics'), {
        propertyId,
        userId,
        eventType: 'view',
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }

  // Track a property click
  async trackPropertyClick(propertyId) {
    try {
      const userId = localStorage.getItem('userId') || 'anonymous';
      
      await addDoc(collection(db, 'property_analytics'), {
        propertyId,
        userId,
        eventType: 'click',
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  }

  // Get statistics for a property
  async getPropertyStats(propertyId) {
    try {
      const viewsQuery = query(
        collection(db, 'property_analytics'),
        where('propertyId', '==', propertyId),
        where('eventType', '==', 'view')
      );
      
      const clicksQuery = query(
        collection(db, 'property_analytics'),
        where('propertyId', '==', propertyId),
        where('eventType', '==', 'click')
      );

      const [viewsSnapshot, clicksSnapshot] = await Promise.all([
        getDocs(viewsQuery),
        getDocs(clicksQuery)
      ]);

      const views = viewsSnapshot.docs;
      const clicks = clicksSnapshot.docs;
      
      const uniqueViewers = new Set(views.map(doc => doc.data().userId));
      const uniqueClickers = new Set(clicks.map(doc => doc.data().userId));

      return {
        propertyId,
        totalViews: views.length,
        totalClicks: clicks.length,
        uniqueViewers: uniqueViewers.size,
        uniqueClickers: uniqueClickers.size
      };
    } catch (error) {
      console.error('Error getting property stats:', error);
      return {
        propertyId,
        totalViews: 0,
        totalClicks: 0,
        uniqueViewers: 0,
        uniqueClickers: 0
      };
    }
  }

  // Get statistics for all properties of a landlord
  async getLandlordStats(landlordId) {
    try {
      // First get all properties
      const propertiesQuery = query(
        collection(db, 'koten'),
        where('verhuurderID', '==', landlordId)
      );
      
      const propertiesSnapshot = await getDocs(propertiesQuery);
      const propertyIds = propertiesSnapshot.docs.map(doc => doc.id);
      
      // Get stats for each property
      const statsPromises = propertyIds.map(id => this.getPropertyStats(id));
      const statsArray = await Promise.all(statsPromises);
      
      const stats = {};
      statsArray.forEach(stat => {
        stats[stat.propertyId] = stat;
      });
      
      return stats;
    } catch (error) {
      console.error('Error getting landlord stats:', error);
      return {};
    }
  }

  // Get revenue data for a landlord
  async getRevenueData(landlordId) {
    try {
      const contractsQuery = query(
        collection(db, 'contracts'),
        where('landlordID', '==', landlordId)
      );
      
      const snapshot = await getDocs(contractsQuery);
      const docs = snapshot.docs.filter(doc => {
        const status = doc.data().status;
        return ['fullySigned', 'signedByTenant', 'signedByLandlord', 'accepted'].includes(status);
      });

      let totalRevenue = 0;
      let monthlyRevenue = 0;
      const activeContracts = docs.length;

      docs.forEach(doc => {
        const data = doc.data();
        const rent = data.rent || 0;
        monthlyRevenue += rent;

        // Calculate total revenue
        if (data.startDate) {
          const startDate = data.startDate.toDate ? data.startDate.toDate() : new Date(data.startDate);
          const monthsSinceStart = Math.max(0, Math.floor((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30)));
          totalRevenue += rent * monthsSinceStart;
        }
      });

      return {
        totalRevenue,
        monthlyRevenue,
        activeContracts
      };
    } catch (error) {
      console.error('Error getting revenue data:', error);
      return {
        totalRevenue: 0,
        monthlyRevenue: 0,
        activeContracts: 0
      };
    }
  }
}

export default new PropertyAnalyticsService();

